import React, { useEffect, useState, useRef } from 'react';
import AudioPlayChip from '../AudioPlayChip';

// The AI column carries the advance-check dot, so its heading carries the key
// to it. A colour with no legend is a colour nobody can read.
const COLUMNS = [
  { label: 'Seq' },
  { label: 'Question' },
  { label: 'Question text' },
  { label: 'Recipient' },
  {
    label: 'AI',
    hint: [
      'The dot beside each AI reply checks the AI’s decision against what the survey actually did next.',
      'Green — they agree: the AI said move on and the survey moved on, or it said stay and the survey stayed.',
      'Red — they disagree: what happened next is not what the AI decided.',
      'Grey — nothing to check: this turn recorded no advance-or-hold decision, or it is the last exchange, so there is no next question to compare with.',
      'Hover a dot for the reason it is that colour.',
    ].join('\n\n'),
  },
  { label: 'Prompts' },
];

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
  fontFamily: 'var(--font-family-primary, sans-serif)'
};

const thStyle = {
  position: 'sticky',
  // The Explorer toolbar sits outside this table's scroll container.
  top: 0,
  zIndex: 10,
  background: '#FFFFFF',
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid #E8EAED',
  color: '#5F6368',
  fontWeight: 600,
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '6px 8px',
  borderBottom: '1px solid #F1F3F4',
  verticalAlign: 'top'
};

const smallButtonStyle = {
  fontSize: 12,
  padding: '2px 8px',
  border: '1px solid #DADCE0',
  borderRadius: 4,
  background: '#FFFFFF',
  cursor: 'pointer'
};

const subtleStyle = {
  fontSize: 11,
  color: '#94a3b8'
};

const resizeHandleStyle = {
  position: 'absolute',
  top: 0,
  right: 0,
  width: 6,
  height: '100%',
  cursor: 'col-resize',
  userSelect: 'none'
};

/**
 * What the dot beside an AI reply means, in words.
 *
 * The check is: the AI decided either to move on to the next question or to
 * stay on this one; the next exchange shows what the survey actually did. Green
 * is agreement, red is disagreement, grey is "there was nothing to compare".
 *
 * `expected` and `actual` are true (move on), false (stay), or null (unknown).
 */
export function advanceStatus(expected, actual) {
  if (expected === null || expected === undefined) {
    return {
      color: '#9ca3af',
      headline: 'Not checked',
      detail: 'This turn has no recorded decision to move on or stay, so there is nothing to compare it with.',
    };
  }
  if (actual === null || actual === undefined) {
    return {
      color: '#9ca3af',
      headline: 'Not checked',
      detail: expected
        ? 'The AI decided to move on to the next question. This is the last exchange in the take, so there is no next question to compare with.'
        : 'The AI decided to stay on this question. This is the last exchange in the take, so there is nothing after it to compare with.',
    };
  }
  if (expected === actual) {
    return {
      color: '#4CAF50',
      headline: 'The survey did what the AI decided',
      detail: expected
        ? 'The AI decided to move on, and the next exchange is a different question.'
        : 'The AI decided to stay on this question, and the next exchange is still the same question.',
    };
  }
  return {
    color: '#F44336',
    headline: 'The survey did not do what the AI decided',
    detail: expected
      ? 'The AI decided to move on, but the next exchange is still the same question.'
      : 'The AI decided to stay on this question, but the next exchange moved to a different question.',
  };
}

const dotPopupStyle = {
  position: 'absolute',
  top: 16,
  left: 0,
  zIndex: 40,
  width: 260,
  padding: '8px 10px',
  background: '#202124',
  color: '#FFFFFF',
  borderRadius: 6,
  fontSize: 12,
  lineHeight: 1.4,
  boxShadow: '0 4px 12px rgba(0,0,0,.28)',
  pointerEvents: 'none',
};

/**
 * The advance-check dot. It explains itself on hover or keyboard focus, because
 * a colour on its own tells nobody why it is that colour.
 */
function AdvanceDot({ status }) {
  const [open, setOpen] = useState(false);
  const text = `${status.headline}. ${status.detail}`;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <span
        role="img"
        tabIndex={0}
        aria-label={text}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: status.color,
          cursor: 'help',
        }}
      />
      {open && (
        <span style={dotPopupStyle}>
          <strong style={{ display: 'block', marginBottom: 4 }}>{status.headline}</strong>
          {status.detail}
        </span>
      )}
    </span>
  );
}

/** The column heading's "why" marker — same explanation, one per column. */
function HeaderHint({ hint }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', marginLeft: 4 }}>
      <span
        role="img"
        tabIndex={0}
        aria-label={hint}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{ cursor: 'help', color: '#5F6368', fontWeight: 400 }}
      >ⓘ</span>
      {open && (
        <span style={{ ...dotPopupStyle, top: 18, width: 300, whiteSpace: 'pre-wrap', fontWeight: 400 }}>{hint}</span>
      )}
    </span>
  );
}

function HighlightText({ text, query }) {
  const value = String(text || '');
  const needle = String(query || '').trim();
  if (!needle) return value;
  const lower = value.toLowerCase();
  const index = lower.indexOf(needle.toLowerCase());
  if (index < 0) return value;
  return <>{value.slice(0, index)}<mark style={{ background: '#FFF3B0', padding: 0 }}>{value.slice(index, index + needle.length)}</mark>{value.slice(index + needle.length)}</>;
}

export default function TimelineExchangeTable({
  sid = '',
  exchanges = [],
  promptsByExchange = {},
  sessionPrompts = {},
  napByExchange = {},
  textByKey = {},
  // question key → { number, variable }: how this question is named to the
  // author. Empty for a take whose survey structure could not be read, and the
  // kernel id stands alone then.
  identityByKey = {},
  audioByQuestion = {},
  helpers = {},
  searchQuery = '',
  focusHit = null,
  onOpenNap = () => {},
  onOpenPrompt = () => {}
}) {
  const [colWidths, setColWidths] = useState(() => COLUMNS.map(() => undefined));
  const thRefs = useRef([]);
  const dragRef = useRef(null);
  const rowRefs = useRef({});

  useEffect(() => {
    if (!focusHit) return;
    rowRefs.current[focusHit.exchangeSeq]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusHit]);

  if (!exchanges || exchanges.length === 0) {
    return null;
  }

  const handleMouseDown = (e, index) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = colWidths[index] ?? thRefs.current[index]?.getBoundingClientRect().width ?? 120;
    dragRef.current = { index, startX, startWidth };

    const handleMouseMove = (moveEvent) => {
      if (!dragRef.current) return;
      const { index: dragIdx, startX: sX, startWidth: sW } = dragRef.current;
      const deltaX = moveEvent.clientX - sX;
      const newWidth = Math.max(48, sW + deltaX);
      setColWidths(prev => {
        const next = [...prev];
        next[dragIdx] = newWidth;
        return next;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      dragRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const latestAiMeta = helpers.latestAiMeta || (() => null);
  const summarizeHuman = helpers.summarizeHuman || (() => '');
  const summarizeSam = helpers.summarizeSam || (() => '');
  const humanizeBytes = helpers.humanizeBytes || ((b) => `${b} B`);

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          {COLUMNS.map((col, i) => (
            <th
              key={col.label}
              ref={el => { thRefs.current[i] = el; }}
              style={{
                ...thStyle,
                width: colWidths[i] != null ? colWidths[i] : undefined
              }}
            >
              {col.label}
              {col.hint && <HeaderHint hint={col.hint} />}
              <div
                style={resizeHandleStyle}
                onMouseDown={(e) => handleMouseDown(e, i)}
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {exchanges.map((row, idx) => {
          const prompts = promptsByExchange?.[row.exchange_seq] || sessionPrompts?.[row.exchange_seq] || [];
          const nap = napByExchange?.[row.exchange_seq];

          const expectedAdvance = (() => {
            if (!nap) return null;
            const nextAction = nap?.nextInstruction?.action || nap?.nextInstruction?.type || '';
            if (nap.shouldAdvanceSurvey === true || String(nextAction).toLowerCase() === 'advance') return true;
            if (nap.shouldAdvanceSurvey === false || String(nextAction).toLowerCase() === 'hold') return false;
            return null;
          })();

          const nextRow = exchanges[idx + 1] || null;

          const actualAdvance = (() => {
            if (!nextRow) return null;
            if (!row.question_id && nextRow.question_id) return true;
            if (!nextRow.question_id) return null;
            return String(nextRow.question_id) !== String(row.question_id);
          })();

          const status = advanceStatus(expectedAdvance, actualAdvance);

          const ai = latestAiMeta(row);
          const evalType = ai?.type ? String(ai.type) : '';
          const evalLabel = evalType === 'clarification' ? 'Clarification' : evalType === 'advancement' ? 'Advance' : evalType;
          const guidance = ai?.nextInstruction ? (ai.nextInstruction.message || ai.nextInstruction.content || ai.nextInstruction.text || '') : '';

          const questionKey = (row.question_id && String(row.question_id)) || '';
          // The author knows this question as "Q2", not as q_1785842391033.
          // Lead with their number, keep the variable name under it, and hold
          // the kernel id back on hover for support work.
          const identity = questionKey ? identityByKey?.[questionKey] : null;
          const questionLabel = identity?.number || identity?.variable || questionKey || (row.exchange_seq === 0 ? 'START' : '');
          const questionSubLabel = identity?.variable && identity.variable !== questionLabel ? identity.variable : '';
          const questionText = questionKey ? (textByKey?.[questionKey] || '') : '';
          const audioPath = questionKey ? audioByQuestion?.[questionKey] : null;

          const samSummary = summarizeSam(row);
          const isFocusedExchange = focusHit?.exchangeSeq === row.exchange_seq;

          return (
            <tr ref={el => { rowRefs.current[row.exchange_seq] = el; }} key={`${sid}-${row.exchange_seq}-${idx}`} style={isFocusedExchange ? { background: '#E8F0FE' } : undefined}>
              <td style={tdStyle}>{row.exchange_seq}</td>
              <td style={tdStyle} title={questionKey ? `Question id: ${questionKey}` : undefined}>
                <div>{questionLabel}</div>
                {questionSubLabel && <div style={subtleStyle}>{questionSubLabel}</div>}
              </td>
              <td style={{ ...tdStyle, whiteSpace: 'normal', wordBreak: 'break-word', color: '#5F6368', fontSize: 12 }}>
                {questionText}
              </td>
              <td style={tdStyle}>
                <div><HighlightText text={summarizeHuman(row)} query={focusHit?.source === 'human' && isFocusedExchange ? searchQuery : ''} /></div>
                {audioPath && <div style={{ marginTop: 6 }}><AudioPlayChip gcsPath={audioPath} /></div>}
              </td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AdvanceDot status={status} />
                    <span style={{ fontSize: 13 }}><HighlightText text={samSummary} query={focusHit?.source === 'ai' && isFocusedExchange ? searchQuery : ''} /></span>
                  </div>

                  {evalLabel && (
                    <div style={subtleStyle}>
                      {evalLabel}
                    </div>
                  )}

                  {guidance && guidance !== samSummary && (
                    <div style={subtleStyle}>
                      {guidance}
                    </div>
                  )}

                  {nap && (
                    <div style={{ marginTop: 6 }}>
                      <button
                        type="button"
                        style={{ ...smallButtonStyle, ...(isFocusedExchange && focusHit?.source === 'nap' ? { borderColor: '#1A73E8', boxShadow: '0 0 0 2px #E8F0FE' } : null) }}
                        title="NAP — Normalized AI Payload: the structured LLM response (extractedData, nextInstruction, shouldAdvanceSurvey, analysis/confidence)"
                        onClick={() => onOpenNap(row.exchange_seq, nap)}
                      >
                        NAP
                      </button>
                    </div>
                  )}
                </div>
              </td>
              <td style={tdStyle}>
                {prompts.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {prompts.map((p, i) => {
                      const sizeLabel = humanizeBytes(p.bytes);
                      const label = sizeLabel ? `[${p.prompt_type || 'text'} - ${sizeLabel}]` : `[${p.prompt_type || 'text'}]`;
                      return (
                        <button
                          key={i}
                          type="button"
                          style={{ ...smallButtonStyle, ...(isFocusedExchange && focusHit?.source === 'prompt' && focusHit?.promptIndex === i ? { borderColor: '#1A73E8', boxShadow: '0 0 0 2px #E8F0FE' } : null) }}
                          onClick={() => onOpenPrompt(row.exchange_seq, p)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
